import { convertTimeToMMSS } from '@src/utility/timeFormat';

import type {
  ReelTranscriptSection,
  ReelTranscriptStructure,
} from './reelTranscriptStructure';

/**
 * PLAT-11253 — the one place a reel, a chapter or a clip is summarised.
 *
 * Both tabs read this: the transcript's heading rows and the Reel Editor's own
 * summary line. It lives in the service layer rather than beside either of them
 * precisely so neither owns it — two implementations of "the same parts in the
 * same order and the same words" is a promise kept only by discipline, and the
 * `'section'` → `'clip'` rename is what it looks like when the discipline is
 * tested.
 *
 * The Reel Editor composes over its own rows (it counts the clips that are
 * **off** as well, which the transcript's structure excludes by construction),
 * so it calls {@link composeReelSummary} directly with counts it has already
 * tallied. The transcript's three scopes are the helpers below it.
 */

const pluralize = (count: number, noun: string) =>
  `${count} ${noun}${count === 1 ? '' : 's'}`;

/**
 * The shape, once: who speaks · how many clips · how long · which chapter, or
 * how many chapters.
 *
 * A part is dropped rather than zeroed when it has nothing to say — a reel
 * before speaker identification states no voices, and an unchaptered reel says
 * nothing rather than "0 chapters". `clipCount` is the one part that always
 * renders, since "how many clips" is the question the line exists to answer.
 *
 * The two chapter parts belong to the two scopes and never meet: a chapter's
 * own line says which one of them it is, the reel's says how many there are.
 */
export function composeReelSummary(input: {
  voiceCount: number;
  clipCount?: number;
  duration: number;
  chapterOrdinal?: number;
  chapterCount?: number;
}): string {
  const parts =
    input.voiceCount > 0 ? [pluralize(input.voiceCount, 'speaker')] : [];
  if (input.clipCount !== undefined) {
    parts.push(pluralize(input.clipCount, 'clip'));
  }
  parts.push(convertTimeToMMSS(input.duration));
  if (input.chapterOrdinal) {
    parts.push(`Chapter ${input.chapterOrdinal}`);
  }
  if (input.chapterCount) {
    parts.push(pluralize(input.chapterCount, 'chapter'));
  }
  return parts.join(' · ');
}

/**
 * The distinct voices across a run of Sections. Two labels resolving to the
 * same person count once; a label nobody has identified still counts as a
 * voice, so the number holds before speaker identification has been done.
 */
function countVoices(
  sections: readonly ReelTranscriptSection[],
  speakersByLabel?: ReadonlyMap<string, { id: string }>,
): number {
  const voices = new Set<string>();
  for (const section of sections) {
    for (const label of section.speakerLabels) {
      voices.add(speakersByLabel?.get(label)?.id ?? label);
    }
  }
  return voices.size;
}

/**
 * What a run of Sections adds up to — the Reel Editor panel's own summary
 * (its `reelSummary`), over the structure the transcript renders.
 *
 * Sections the creator turned off never reach this structure, so there is no
 * "off" tail here — unlike the panel's line, which reports them.
 */
export function reelSectionsSummary(
  sections: readonly ReelTranscriptSection[],
  duration: number,
  speakersByLabel?: ReadonlyMap<string, { id: string }>,
  /** Which chapter this run is, when the run is one — stated last. */
  chapterOrdinal?: number,
): string {
  return composeReelSummary({
    voiceCount: countVoices(sections, speakersByLabel),
    clipCount: sections.length,
    duration,
    chapterOrdinal,
  });
}

/**
 * One Section's line — the same parts in the same order as the runs above it,
 * minus the count a single Section cannot have: who speaks in it, and how long
 * it runs.
 */
export function reelSectionSummary(
  section: ReelTranscriptSection,
  speakersByLabel?: ReadonlyMap<string, { id: string }>,
): string {
  return composeReelSummary({
    voiceCount: countVoices([section], speakersByLabel),
    duration: section.duration,
  });
}

/**
 * The whole reel's line — the same summary at reel scope, with how many
 * Chapters it is in on the end.
 *
 * The count is only ever stated here. A Chapter's own heading carries this
 * summary over its Sections, where "how many chapters" would be answering a
 * question nobody asked of it.
 */
export function reelStructureSummary(
  structure: ReelTranscriptStructure,
  speakersByLabel?: ReadonlyMap<string, { id: string }>,
): string {
  return composeReelSummary({
    voiceCount: countVoices(structure.sections, speakersByLabel),
    clipCount: structure.sections.length,
    duration: structure.totalDuration,
    chapterCount: structure.chapters.length,
  });
}
